(() => {
	const initHero = (root) => {
		const hero = root.querySelector("[data-nmc-hero]");
		const video = hero?.querySelector("[data-nmc-hero-video]");
		if (!hero || !video) return;

		const reduceMotion = matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		let frameId = 0;
		let copyVisible = false;

		const revealCopy = () => {
			if (copyVisible) return;
			copyVisible = true;
			hero.classList.add("is-copy-visible");
		};

		const complete = () => {
			if (frameId) cancelAnimationFrame(frameId);
			frameId = 0;
			revealCopy();
			hero.classList.remove("is-playing");
			hero.classList.add("is-complete");
		};

		const trackPlayback = () => {
			frameId = 0;
			if (
				Number.isFinite(video.duration) &&
				video.duration > 0 &&
				video.currentTime >= Math.max(0, video.duration - 0.5)
			) {
				revealCopy();
				return;
			}
			if (!video.paused && !video.ended)
				frameId = requestAnimationFrame(trackPlayback);
		};

		if (reduceMotion) {
			video.preload = "none";
			video.pause();
			complete();
			return;
		}

		video.muted = true;
		video.defaultMuted = true;
		video.addEventListener("playing", () => {
			hero.classList.add("is-playing");
			if (!frameId && !copyVisible)
				frameId = requestAnimationFrame(trackPlayback);
		});
		video.addEventListener("ended", complete, { once: true });
		video.addEventListener("error", complete, { once: true });

		try {
			const playback = video.play();
			if (playback) playback.catch(complete);
		} catch (error) {
			complete();
		}
	};

	const initAndroid = (root) => {
		const slidesWrap = root.querySelector("[data-nmc-android-slides]");
		const featuresWrap = root.querySelector("[data-android-features]");
		const pager = root.querySelector("[data-nmc-android-pager]");
		if (!slidesWrap || !featuresWrap || !pager) return;

		const slides = [
			...slidesWrap.querySelectorAll("[data-nmc-android-slide]"),
		];
		const dots = [...pager.querySelectorAll("button")];
		const features = [
			...featuresWrap.querySelectorAll(".nmc__android-feature"),
		];
		const reduceMotion = matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		const hasGsap = Boolean(window.gsap);

		if (matchMedia("(max-width: 750px)").matches) {
			features.forEach((feature) => feature.classList.add("is-active"));
			return;
		}

		features.forEach((feature, k) => {
			feature.setAttribute("role", "button");
			feature.setAttribute("tabindex", "0");
			feature.setAttribute("aria-expanded", String(k === 0));
		});

		let current = 0;
		let tween = null;
		let rubberTween = null;
		const cross = { p: 0, dir: 1, from: null, to: null };

		const lerp = (a, b, t) => a + (b - a) * t;

		const hideSlide = (slide) =>
			gsap.set(slide, { autoAlpha: 0, xPercent: 0 });

		const render = () => {
			if (!cross.from || !cross.to) return;
			const out = -10 * cross.dir;
			const inn = 10 * cross.dir;
			gsap.set(cross.from, {
				autoAlpha: lerp(1, 0, cross.p),
				xPercent: lerp(0, out, cross.p),
			});
			gsap.set(cross.to, {
				autoAlpha: lerp(0, 1, cross.p),
				xPercent: lerp(inn, 0, cross.p),
			});
		};

		// Slides that hold a video keep it paused while off-screen; the
		// neighbouring slide warms it up so the poster does not linger.
		const syncSlideVideos = () => {
			slides.forEach((slide, k) => {
				const video = slide.querySelector(
					"[data-nmc-android-slide-video]",
				);
				if (!video) return;
				if (k === current) {
					const playback = video.play();
					if (playback) playback.catch(() => {});
				} else {
					video.pause();
					if (Math.abs(k - current) === 1 && !video.dataset.warmed) {
						video.dataset.warmed = "1";
						video.load();
					}
				}
			});
		};

		const syncUI = () => {
			dots.forEach((dot, k) => {
				dot.classList.toggle("is-active", k === current);
				if (k === current) dot.setAttribute("aria-current", "true");
				else dot.removeAttribute("aria-current");
			});
			features.forEach((feature, k) => {
				feature.classList.toggle("is-active", k === current);
				feature.setAttribute("aria-expanded", String(k === current));
			});
			slides.forEach((slide, k) => {
				slide.classList.toggle("is-active", k === current);
				slide.setAttribute(
					"aria-hidden",
					k === current ? "false" : "true",
				);
			});
			syncSlideVideos();
		};

		const stopTweens = () => {
			if (tween) {
				tween.kill();
				tween = null;
			}
			if (rubberTween) {
				rubberTween.kill();
				rubberTween = null;
			}
		};

		const animateTo = (endP, duration) => {
			stopTweens();
			tween = gsap.to(cross, {
				p: endP,
				duration,
				ease: "power3.out",
				onUpdate: render,
				onComplete: () => {
					tween = null;
				},
			});
		};

		const goTo = (index, dirOverride) => {
			const count = slides.length;
			const next = ((index % count) + count) % count;
			if (next === current) return;
			const dir = dirOverride ?? (next > current ? 1 : -1);
			const prevIndex = current;
			current = next;
			syncUI();
			if (!hasGsap || reduceMotion) {
				slides.forEach((slide, k) => {
					slide.classList.toggle("is-active", k === next);
					slide.style.opacity = "";
					slide.style.visibility = "";
					slide.style.transform = "";
				});
				return;
			}
			stopTweens();
			cross.from = slides[prevIndex];
			cross.to = slides[next];
			cross.dir = dir;
			cross.p = 0;
			slides.forEach((slide, k) => {
				if (k !== prevIndex && k !== next) hideSlide(slide);
			});
			render();
			animateTo(1, 0.55);
		};

		dots.forEach((dot, k) => dot.addEventListener("click", () => goTo(k)));
		pager.addEventListener("keydown", (event) => {
			if (event.key === "ArrowRight") {
				event.preventDefault();
				goTo(current + 1);
			} else if (event.key === "ArrowLeft") {
				event.preventDefault();
				goTo(current - 1);
			}
		});
		features.forEach((feature, k) => {
			feature.addEventListener("click", () => goTo(k));
			feature.addEventListener("keydown", (event) => {
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault();
					goTo(k);
				}
			});
		});

		if (!hasGsap || reduceMotion) return;

		let drag = null;
		slidesWrap.addEventListener("pointerdown", (event) => {
			if (event.pointerType === "mouse" && event.button !== 0) return;
			if (tween) {
				tween.progress(1);
				tween = null;
			}
			if (rubberTween) {
				rubberTween.kill();
				rubberTween = null;
			}
			drag = {
				id: event.pointerId,
				startX: event.clientX,
				lastX: event.clientX,
				lastT: performance.now(),
				velocity: 0,
				target: null,
			};
			slidesWrap.classList.add("is-dragging");
			slidesWrap.setPointerCapture(event.pointerId);
		});

		slidesWrap.addEventListener("pointermove", (event) => {
			if (!drag || event.pointerId !== drag.id) return;
			const dx = event.clientX - drag.startX;
			const now = performance.now();
			drag.velocity =
				(event.clientX - drag.lastX) / Math.max(1, now - drag.lastT);
			drag.lastX = event.clientX;
			drag.lastT = now;
			if (Math.abs(dx) < 4) return;

			const dir = dx < 0 ? 1 : -1;
			const targetIndex = current + dir;
			const boxWidth = slidesWrap.clientWidth;

			if (targetIndex < 0 || targetIndex >= slides.length) {
				drag.target = null;
				cross.from = null;
				cross.to = null;
				const shift = (dx / boxWidth) * 100 * 0.18;
				gsap.set(slides[current], {
					autoAlpha: 1,
					xPercent: Math.max(-4, Math.min(4, shift)),
				});
				return;
			}

			if (drag.target !== targetIndex) {
				drag.target = targetIndex;
				cross.dir = dir;
				cross.from = slides[current];
				cross.to = slides[targetIndex];
				slides.forEach((slide, k) => {
					if (k !== current && k !== targetIndex) hideSlide(slide);
				});
			}
			cross.p = Math.min(Math.abs(dx) / (boxWidth * 0.22), 1);
			render();
		});

		const endDrag = (event) => {
			if (!drag || event.pointerId !== drag.id) return;
			const { startX, velocity, target } = drag;
			const dx = event.clientX - startX;
			drag = null;
			slidesWrap.classList.remove("is-dragging");
			if (Math.abs(dx) < 4) return;

			if (target === null) {
				rubberTween = gsap.to(slides[current], {
					autoAlpha: 1,
					xPercent: 0,
					duration: 0.4,
					ease: "power3.out",
				});
				return;
			}

			const flick = Math.abs(velocity) > 0.45;
			const crossed = Math.abs(dx) > slidesWrap.clientWidth * 0.12;
			if ((flick || crossed) && cross.to === slides[target]) {
				current = target;
				syncUI();
				animateTo(1, Math.max(0.2, (1 - cross.p) * 0.45));
			} else {
				animateTo(0, 0.35);
			}
		};

		slidesWrap.addEventListener("pointerup", endDrag);
		slidesWrap.addEventListener("pointercancel", endDrag);
	};

	const initInsights = (root) => {
		const slider = root.querySelector("[data-nmc-insight-splide]");
		if (!slider || !window.Splide || slider.dataset.ready === "true")
			return;
		if (matchMedia("(max-width: 1024px)").matches) return;

		slider.dataset.ready = "true";
		const instance = new Splide(slider, {
			type: "slide",
			fixedWidth: "calc(var(--count-width) * 411.643)",
			gap: "calc(var(--count-width) * 64)",
			perPage: 3,
			perMove: 1,
			speed: matchMedia("(prefers-reduced-motion: reduce)").matches
				? 0
				: 650,
			rewind: false,
			pagination: false,
			arrows: true,
			drag: true,
			trimSpace: true,
			breakpoints: {
				750: {
					fixedWidth: "calc(var(--count-width) * 300)",
					gap: "calc(var(--count-width) * 12)",
				},
			},
		});
		const arrows = slider.querySelectorAll(".splide__arrow");
		const syncArrowState = (...args) => {
			if (arrows.length !== 2) return;
			const index =
				typeof args[0] === "number" ? args[0] : instance.index;
			const atStart = index <= 0;
			const atEnd = index >= instance.Components.Controller.getEnd();
			arrows[0].disabled = atStart;
			arrows[1].disabled = atEnd;
			arrows[0].setAttribute("aria-disabled", String(atStart));
			arrows[1].setAttribute("aria-disabled", String(atEnd));
		};
		instance.on("move", syncArrowState);
		instance.on("moved", syncArrowState);
		instance.mount();
	};

	const initUnboxing = (root) => {
		const section = root.querySelector(".nmc__unboxing");
		const video = section?.querySelector("[data-nmc-unboxing-video]");
		if (!section || !video || video.dataset.ready === "true") return;

		video.dataset.ready = "true";
		const reduceMotion = matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		const markReady = () => section.classList.add("is-video-ready");
		const playVideo = () => {
			const playback = video.play();
			if (playback) playback.catch(() => {});
		};

		video.addEventListener("loadeddata", markReady, { once: true });
		if (video.readyState >= 2) markReady();

		const activate = () => {
			video.preload = "auto";
			video.load();
			if (!reduceMotion) playVideo();
		};

		if (!("IntersectionObserver" in window)) {
			activate();
			return;
		}

		const observer = new IntersectionObserver(
			(entries, self) => {
				if (!entries.some((entry) => entry.isIntersecting)) return;
				self.disconnect();
				activate();
			},
			{ rootMargin: "100% 0px" },
		);
		observer.observe(section);
	};

	const applyBackgrounds = (el) => {
		let map;
		try {
			map = JSON.parse(el.dataset.nmcBg);
		} catch (error) {
			el.removeAttribute("data-nmc-bg");
			return;
		}
		Object.keys(map).forEach((name) => {
			if (map[name])
				el.style.setProperty(name, 'url("' + map[name] + '")');
		});
		el.removeAttribute("data-nmc-bg");
	};

	const initLazyBackgrounds = () => {
		const targets = document.querySelectorAll("[data-nmc-bg]");
		if (!targets.length) return;
		if (!("IntersectionObserver" in window)) {
			targets.forEach(applyBackgrounds);
			return;
		}
		const observer = new IntersectionObserver(
			(entries, self) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) return;
					self.unobserve(entry.target);
					applyBackgrounds(entry.target);
				});
			},
			// one viewport of lead time: decoded before it can be seen
			{ rootMargin: "100% 0px" },
		);
		targets.forEach((el) => observer.observe(el));
	};

	const initColorVideo = (root) => {
		const media = root.querySelector("[data-nmc-color-video]");
		const video = media?.querySelector(".nmc__color-video");
		if (!media || !video) return;

		const showPlaying = () => {
			media.classList.remove("is-ended");
			media.classList.add("is-playing");
		};
		const showLastFrame = () => {
			media.classList.remove("is-playing");
			media.classList.add("is-ended");
		};

		if (matchMedia("(prefers-reduced-motion: reduce)").matches) {
			return;
		}

		video.muted = true;
		video.addEventListener("playing", showPlaying, { once: true });
		video.addEventListener("ended", showLastFrame, { once: true });

		let started = false;
		const play = () => {
			if (started) return;
			started = true;
			video.load();
			const playPromise = video.play();
			if (playPromise) playPromise.catch(() => {});
		};

		if (!("IntersectionObserver" in window)) {
			play();
			return;
		}

		const observer = new IntersectionObserver(
			(entries, self) => {
				if (!entries.some((entry) => entry.isIntersecting)) return;
				self.disconnect();
				play();
			},
			{ rootMargin: "0px 0px -33.34% 0px" },
		);
		observer.observe(media);
	};

	const initPhotoAccordion = (root) => {
		if (!matchMedia("(min-width: 1025px)").matches) return;

		const slider = root.querySelector("[data-nmc-photo-accordion]");
		const track = slider?.querySelector(".splide__track");
		const list = slider?.querySelector(".splide__list");
		const cards = list
			? [...list.querySelectorAll(".nmc__photo-slide")]
			: [];
		const previous = slider?.querySelector(".splide__arrow--prev");
		const next = slider?.querySelector(".splide__arrow--next");

		if (
			!slider ||
			!track ||
			!list ||
			cards.length !== 3 ||
			!previous ||
			!next ||
			slider.dataset.nmcPhotoAccordionReady === "true"
		)
			return;

		slider.dataset.nmcPhotoAccordionReady = "true";
		slider.classList.add("is-photo-accordion");
		list.setAttribute("aria-live", "polite");
		previous.removeAttribute("disabled");
		next.removeAttribute("disabled");
		previous.setAttribute("aria-disabled", "false");
		next.setAttribute("aria-disabled", "false");

		const reduceMotion = matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		const ACTIVE_WIDTH = 1040;
		const INACTIVE_WIDTH = 650;
		const GAP = 24;
		const SECOND_X = ACTIVE_WIDTH + GAP;
		const THIRD_X = SECOND_X + INACTIVE_WIDTH + GAP;
		const DRAG_DISTANCE = 320;
		let order = cards.slice();
		let direction = 1;
		let progress = 0;
		let animationFrame = 0;
		let running = false;
		let drag = null;
		let suppressClickUntil = 0;

		const mix = (from, to, amount) => from + (to - from) * amount;
		const clean = (value) => Math.round(value * 1000) / 1000;
		const countWidth = () => Math.min(innerWidth, 2560) / 1920;

		const place = (card, x, width, opacity, zIndex) => {
			card.style.width = `calc(var(--count-width) * ${clean(width)})`;
			card.style.transform = `translate3d(calc(var(--count-width) * ${clean(x)}), 0, 0)`;
			card.style.opacity = String(clean(opacity));
			card.style.zIndex = String(zIndex);
		};

		const updateState = () => {
			cards.forEach((card) => {
				const active = card === order[0];
				card.classList.toggle("is-active", active);
				card.classList.toggle("is-inactive", !active);
				card.setAttribute("aria-pressed", String(active));
				if (active) card.setAttribute("aria-current", "true");
				else card.removeAttribute("aria-current");
			});
		};

		const renderResting = () => {
			place(order[0], 0, ACTIVE_WIDTH, 1, 3);
			place(order[1], SECOND_X, INACTIVE_WIDTH, 1, 2);
			place(order[2], THIRD_X, INACTIVE_WIDTH, 1, 1);
			updateState();
		};

		const renderTransition = (amount) => {
			const outgoing = order[0];
			const following = order[1];
			const preceding = order[2];

			if (direction > 0) {
				// Offset the collapsing card by the gap so its visible edge stays a
				// fixed distance from the following card throughout the transition.
				place(
					outgoing,
					mix(0, -GAP, amount),
					mix(ACTIVE_WIDTH, 0, amount),
					1,
					4,
				);
				place(
					following,
					mix(SECOND_X, 0, amount),
					mix(INACTIVE_WIDTH, ACTIVE_WIDTH, amount),
					1,
					3,
				);
				place(
					preceding,
					mix(THIRD_X, SECOND_X, amount),
					INACTIVE_WIDTH,
					1,
					2,
				);
				return;
			}

			// Going back is the forward move run in reverse: the card arriving
			// from the end grows out of nothing at the left while the active
			// one slides right and shrinks into the second slot. Collapsing the
			// active card here instead would leave that slot empty, which is
			// what the stand-in clone used to paper over -- at the cost of
			// showing the same photo twice for the length of the animation.
			place(
				preceding,
				mix(-GAP, 0, amount),
				mix(0, ACTIVE_WIDTH, amount),
				1,
				4,
			);
			place(
				outgoing,
				mix(0, SECOND_X, amount),
				mix(ACTIVE_WIDTH, INACTIVE_WIDTH, amount),
				1,
				3,
			);
			place(
				following,
				mix(SECOND_X, THIRD_X, amount),
				INACTIVE_WIDTH,
				1,
				2,
			);
		};

		const finishTransition = () => {
			if (direction > 0) order = [order[1], order[2], order[0]];
			else order = [order[2], order[0], order[1]];
			progress = 0;
			running = false;
			renderResting();
		};

		const animateTo = (target) => {
			if (animationFrame) cancelAnimationFrame(animationFrame);
			const from = progress;
			const distance = Math.abs(target - from);

			if (reduceMotion || distance < 0.001) {
				progress = target;
				renderTransition(progress);
				if (target === 1) finishTransition();
				else {
					running = false;
					renderResting();
				}
				return;
			}

			running = true;
			const startedAt = performance.now();
			const duration = Math.max(180, 650 * distance);
			const tick = (now) => {
				const elapsed = Math.min(1, (now - startedAt) / duration);
				const eased = 1 - (1 - elapsed) ** 3;
				progress = mix(from, target, eased);
				renderTransition(progress);

				if (elapsed < 1) {
					animationFrame = requestAnimationFrame(tick);
					return;
				}

				animationFrame = 0;
				if (target === 1) finishTransition();
				else {
					running = false;
					progress = 0;
					renderResting();
				}
			};
			animationFrame = requestAnimationFrame(tick);
		};

		const move = (nextDirection) => {
			if (running || drag) return;
			direction = nextDirection;
			progress = 0;
			renderTransition(progress);
			animateTo(1);
		};

		cards.forEach((card) => {
			card.setAttribute("role", "button");
			card.setAttribute("tabindex", "0");
			card.querySelectorAll("img").forEach((image) => {
				image.draggable = false;
			});
			card.addEventListener("click", () => {
				if (performance.now() < suppressClickUntil || running || drag)
					return;
				const index = order.indexOf(card);
				if (index === 1) move(1);
				else if (index === 2) move(-1);
			});
			card.addEventListener("keydown", (event) => {
				if (event.key !== "Enter" && event.key !== " ") return;
				event.preventDefault();
				card.click();
			});
		});

		previous.addEventListener("click", () => move(-1));
		next.addEventListener("click", () => move(1));
		slider.addEventListener("keydown", (event) => {
			if (event.key === "ArrowLeft") {
				event.preventDefault();
				move(-1);
			} else if (event.key === "ArrowRight") {
				event.preventDefault();
				move(1);
			}
		});

		track.addEventListener("pointerdown", (event) => {
			if (
				running ||
				(event.pointerType === "mouse" && event.button !== 0)
			)
				return;
			drag = {
				id: event.pointerId,
				startX: event.clientX,
				lastX: event.clientX,
				lastTime: performance.now(),
				velocity: 0,
				direction: 0,
			};
			slider.classList.add("is-dragging");
			track.setPointerCapture(event.pointerId);
		});

		track.addEventListener("pointermove", (event) => {
			if (!drag || event.pointerId !== drag.id) return;
			const delta = event.clientX - drag.startX;
			const now = performance.now();
			drag.velocity =
				(event.clientX - drag.lastX) / Math.max(1, now - drag.lastTime);
			drag.lastX = event.clientX;
			drag.lastTime = now;
			if (Math.abs(delta) < 6) return;

			const nextDirection = delta < 0 ? 1 : -1;
			if (drag.direction !== nextDirection) {
				drag.direction = nextDirection;
				direction = nextDirection;
				progress = 0;
				renderResting();
			}

			progress = Math.min(
				0.96,
				Math.abs(delta) / (DRAG_DISTANCE * countWidth()),
			);
			renderTransition(progress);
			if (event.cancelable) event.preventDefault();
		});

		const endDrag = (event, cancelled = false) => {
			if (!drag || event.pointerId !== drag.id) return;
			const moved = Math.abs(event.clientX - drag.startX);
			const releaseVelocity =
				performance.now() - drag.lastTime > 120
					? 0
					: Math.abs(drag.velocity);
			const commit =
				!cancelled &&
				drag.direction !== 0 &&
				(progress >= 0.22 || releaseVelocity > 0.45);
			if (track.hasPointerCapture(event.pointerId))
				track.releasePointerCapture(event.pointerId);
			drag = null;
			slider.classList.remove("is-dragging");

			if (moved > 8) {
				suppressClickUntil = performance.now() + 250;
			}

			if (commit) animateTo(1);
			else if (progress > 0) animateTo(0);
			else renderResting();
		};

		track.addEventListener("pointerup", (event) => endDrag(event));
		track.addEventListener("pointercancel", (event) =>
			endDrag(event, true),
		);

		renderResting();
	};

	// Scroll-driven image sequence for the accessories section. The section is
	// pinned at the top of the viewport, and scroll progress is mapped first
	// onto the 66 frames, which are drawn into a canvas so no layout or image
	// decode happens while scrubbing, and then onto the feature copy that
	// reveals around the device once the sequence has played out.
	const initSequence = (root) => {
		const section = root.querySelector("[data-nmc-sequence]");
		if (!section) return;
		const canvas = section.querySelector("[data-nmc-sequence-canvas]");
		if (!canvas) return;

		const total = parseInt(section.dataset.nmcSequenceFrames, 10);
		const isMobile = innerWidth <= 750;
		const template = isMobile
			? section.dataset.nmcSequenceMb
			: innerWidth <= 1024
				? section.dataset.nmcSequencePad
				: section.dataset.nmcSequencePc;
		if (!total || !template) return;

		// Without a scrubber there is nothing to animate, and with reduced
		// motion there should not be: the poster frame is left in place and the
		// frame set is never fetched.
		if (
			!window.gsap ||
			!window.ScrollTrigger ||
			matchMedia("(prefers-reduced-motion: reduce)").matches
		)
			return;

		// The frames carry their own transparency and the section backdrop shows
		// through them, so the canvas has to keep an alpha channel: an opaque
		// one would composite every frame onto black.
		const ctx = canvas.getContext("2d", { alpha: true });
		if (!ctx) return;
		gsap.registerPlugin(ScrollTrigger);
		// Mobile browsers resize the viewport when the address bar collapses;
		// refreshing on that would make the pinned band jump mid-scroll.
		ScrollTrigger.config({ ignoreMobileResize: true });

		// The feature copy is plain markup and readable on its own. Only now
		// that the scrubber is certain to run do we lift it onto the device and
		// take over its visibility, so no-JS and reduced motion keep the static
		// layout instead of a section of invisible text.
		const stageA = section.querySelectorAll('[data-nmc-stage="a"]');
		const stageB = section.querySelectorAll('[data-nmc-stage="b"]');
		const staged = [...stageA, ...stageB];
		section.classList.add("is-sequence-staged");

		// One timeline for the whole reveal, scrubbed from the pinned trigger's
		// own progress rather than a second trigger, so the pin and the copy
		// cannot drift apart. Mobile plays its two stages in the same place
		// under the device, so the first one has to leave before the second
		// arrives.
		let revealTl = null;
		if (staged.length) {
			// Same fade-and-lift vocabulary as the page's other reveals.
			const entry = () => ({
				autoAlpha: 1,
				yPercent: 0,
				duration: 0.75,
				ease: "power2.out",
				stagger: 0.06,
			});
			gsap.set(staged, { autoAlpha: 0, yPercent: 8 });
			revealTl = gsap.timeline({ paused: true });
			if (isMobile) {
				revealTl
					.to(stageA, entry())
					.to({}, { duration: 0.5 })
					.to(stageA, {
						autoAlpha: 0,
						yPercent: -8,
						duration: 0.5,
						ease: "power2.in",
					})
					.to(stageB, entry(), "-=0.15")
					.to({}, { duration: 0.4 });
			} else {
				revealTl.to(staged, entry()).to({}, { duration: 0.5 });
			}
		}

		// Scroll budget: the frames keep the distance they always had and the
		// reveal is bought on top of it, twice over on mobile because it plays
		// two stages rather than one.
		const scrollPlan = () => {
			const sequence = Math.max(
				700,
				Math.min(1600, Math.round(innerHeight * 1.2)),
			);
			const reveal =
				Math.max(420, Math.min(900, Math.round(innerHeight * 0.7))) *
				(isMobile ? 2 : 1);
			return { sequence, total: sequence + reveal };
		};

		const frames = new Array(total);
		const srcFor = (index) =>
			template.replace("{i}", String(index).padStart(5, "0"));

		// While the tail of the sequence is still downloading, fall back to the
		// closest frame that is ready rather than holding the last drawn one.
		const nearestReady = (index) => {
			for (let step = 0; step < total; step += 1) {
				const back = frames[index - step];
				if (back && back.complete && back.naturalWidth) return back;
				const forward = frames[index + step];
				if (forward && forward.complete && forward.naturalWidth)
					return forward;
			}
			return null;
		};

		let target = 0;
		let painted = null;
		const draw = () => {
			const frame = nearestReady(target);
			if (!frame || frame === painted) return;
			if (canvas.width !== frame.naturalWidth) {
				canvas.width = frame.naturalWidth;
				canvas.height = frame.naturalHeight;
			} else {
				// Transparent frames composite onto whatever is already there,
				// so the previous one has to go first or they stack up.
				ctx.clearRect(0, 0, canvas.width, canvas.height);
			}
			ctx.drawImage(frame, 0, 0, canvas.width, canvas.height);
			painted = frame;
			if (!section.classList.contains("is-sequence-ready"))
				section.classList.add("is-sequence-ready");
		};

		let queued = 0;
		const queueDraw = () => {
			if (queued) return;
			queued = requestAnimationFrame(() => {
				queued = 0;
				draw();
			});
		};

		// Frames are fetched in order through a small pool so the opening of the
		// sequence is playable long before the tail lands, and so the browser is
		// never asked for 66 connections at once.
		const CONCURRENCY = 6;
		let next = 0;
		let inFlight = 0;
		const pump = () => {
			while (inFlight < CONCURRENCY && next < total) {
				const index = next;
				next += 1;
				inFlight += 1;
				const frame = new Image();
				frame.decoding = "async";
				frame.onload = () => {
					inFlight -= 1;
					queueDraw();
					pump();
				};
				frame.onerror = () => {
					inFlight -= 1;
					// A missing opening frame means the set is unusable: drop
					// the pin so the page keeps its normal scroll length and
					// the poster stays on screen, and hand the feature copy
					// back to the static layout rather than leave it hidden.
					if (index === 0) {
						next = total;
						trigger.kill(true);
						if (revealTl) {
							revealTl.kill();
							gsap.set(staged, { clearProps: "all" });
						}
						section.classList.remove("is-sequence-staged");
						return;
					}
					pump();
				};
				frame.src = srcFor(index);
				frames[index] = frame;
			}
		};

		const trigger = ScrollTrigger.create({
			trigger: section,
			// At one viewport tall the section has to take the top of the screen
			// the moment it gets there; holding out for its bottom edge would
			// pin it with a strip of the section above still on. Once the
			// reveal copy has grown it past a viewport that inverts: pinning
			// from the bottom is what keeps the tail of the copy on screen.
			start: () =>
				section.offsetHeight > innerHeight
					? "bottom bottom"
					: "top top",
			end: () => "+=" + scrollPlan().total,
			pin: true,
			pinSpacing: true,
			anticipatePin: 1,
			invalidateOnRefresh: true,
			scrub: 0.4,
			onUpdate: (self) => {
				const plan = scrollPlan();
				const split = plan.sequence / plan.total;
				const progress = self.progress;
				const index = Math.min(
					total - 1,
					Math.round(Math.min(1, progress / split) * (total - 1)),
				);
				if (index !== target) {
					target = index;
					draw();
				}
				if (revealTl)
					revealTl.progress(
						progress <= split
							? 0
							: (progress - split) / (1 - split),
					);
			},
		});

		// Only start paying for the frame set once the section is roughly a
		// viewport away, matching the lazy background loader above.
		if ("IntersectionObserver" in window) {
			const observer = new IntersectionObserver(
				(entries, self) => {
					if (!entries.some((entry) => entry.isIntersecting)) return;
					self.disconnect();
					pump();
				},
				{ rootMargin: "100% 0px" },
			);
			observer.observe(section);
		} else {
			pump();
		}
	};

	const init = () => {
		const root = document.querySelector("[data-nmc]");
		if (!root || root.dataset.ready === "true") return;
		root.dataset.ready = "true";
		initLazyBackgrounds();
		initHero(root);
		initColorVideo(root);
		initAndroid(root);
		initInsights(root);
		initUnboxing(root);
		initPhotoAccordion(root);
		initSequence(root);
		if (window.Splide) {
			root.querySelectorAll("[data-nmc-splide]").forEach((slider) => {
				if (slider.dataset.nmcPhotoAccordionReady === "true") return;
				const opts = {
					type: "slide",
					perMove: 1,
					autoWidth: true,
					gap: "calc(var(--count-width) * 24)",
					padding: {
						left: "calc(var(--count-width) * 428)",
						right: "calc(var(--count-width) * 240)",
					},
					pagination: false,
					breakpoints: {
						1024: {
							padding: {
								left: "calc(var(--count-width) * 154.14)",
								right: "calc(var(--count-width) * 32)",
							},
						},
						750: {
							gap: "calc(var(--count-width) * 12)",
							padding: {
								left: "calc(var(--count-width) * 62.2)",
								right: "calc(var(--count-width) * 16)",
							},
						},
					},
				};

				const sliderRoot = slider.closest(".nmc__cloud") || root;
				const verticalLabel = sliderRoot.querySelector(
					".nmc__vertical-label-img",
				);
				const list = slider.querySelector(".splide__list");
				const countWidth = () => Math.min(innerWidth, 2560) / 1920;
				const fadeLabel = () => {
					if (!verticalLabel || !list) return;
					const raw =
						list.style.transform ||
						list.style.getPropertyValue("--splide-transform") ||
						"";
					const match = raw.match(/-?\d*\.?\d+/);
					const fade = 100 * countWidth();
					verticalLabel.style.opacity = String(
						Math.max(
							0,
							1 -
								Math.abs(match ? parseFloat(match[0]) : 0) /
									fade,
						).toFixed(3),
					);
				};
				const instance = new Splide(slider, opts);
				if (verticalLabel) {
					instance.on("move", fadeLabel);
					instance.on("moved", fadeLabel);
				}

				const arrows = slider.querySelectorAll(".splide__arrow");
				const keepArrowsLive = () => {
					arrows.forEach((arrow) => {
						if (!arrow.disabled) return;
						arrow.disabled = false;
						arrow.setAttribute("aria-disabled", "false");
					});
				};
				instance.on("moved", keepArrowsLive);
				instance.mount();
			});
		}
		if (
			window.gsap &&
			window.ScrollTrigger &&
			!matchMedia("(prefers-reduced-motion: reduce)").matches
		) {
			gsap.registerPlugin(ScrollTrigger);
			root.querySelectorAll(".nmc-reveal").forEach((section) => {
				const itemSelector = section.dataset.nmcRevealItems;
				const sectionItems = itemSelector
					? section.querySelectorAll(itemSelector)
					: section.children;

				gsap.from(sectionItems, {
					autoAlpha: 0,
					yPercent: 8,
					duration: 0.75,
					ease: "power2.out",
					stagger: itemSelector ? 0.06 : 0.08,
					scrollTrigger: {
						trigger: section,
						start: "top 75%",
						once: true,
					},
				});
			});

			root.querySelectorAll("[data-nmc-transfer-cards]").forEach(
				(wrap) => {
					const cards = wrap.querySelectorAll(".nmc__transfer-card");
					if (!cards.length) return;
					gsap.from(cards, {
						autoAlpha: 0,
						yPercent: 14,
						rotation: (index, card) => {
							const angle =
								parseFloat(
									getComputedStyle(card).getPropertyValue(
										"--nmc-card-rotate",
									),
								) || 0;
							return -angle;
						},
						duration: 0.8,
						ease: "power3.out",
						stagger: 0.12,
						transformOrigin: "left top",
						scrollTrigger: {
							trigger: wrap,
							start: "top 78%",
							once: true,
						},
					});
				},
			);

			const colorHanddraw = root.querySelector(
				"[data-nmc-color-handdraw]",
			);
			if (colorHanddraw) {
				gsap.set(colorHanddraw, { "--nmc-color-handdraw-scale": 0 });
				gsap.to(colorHanddraw, {
					"--nmc-color-handdraw-scale": 1,
					duration: 0.9,
					ease: "power2.out",
					scrollTrigger: {
						trigger: colorHanddraw,
						start: "top 50%",
						once: true,
					},
				});
			}

			const cloudCrossline = root.querySelector(
				"[data-nmc-cloud-crossline]",
			);
			if (cloudCrossline) {
				gsap.set(cloudCrossline, {
					clipPath: "inset(0 100% 0 0)",
				});
				gsap.to(cloudCrossline, {
					clipPath: "inset(0 0% 0 0)",
					duration: 3.4,
					ease: "power2.out",
					scrollTrigger: {
						trigger: cloudCrossline,
						start: "top 50%",
						once: true,
					},
				});
			}
		}
	};
	if (document.readyState === "loading")
		document.addEventListener(
			"DOMContentLoaded",
			() => setTimeout(init, 0),
			{ once: true },
		);
	else setTimeout(init, 0);
	addEventListener("load", init, { once: true });

	const getNmcViewportMode = () => {
		if (window.innerWidth <= 750) return "mobile";
		if (window.innerWidth <= 1024) return "tablet";
		return "desktop";
	};

	let nmcViewportMode = getNmcViewportMode();
	let nmcResizeTimer;

	window.addEventListener(
		"resize",
		() => {
			clearTimeout(nmcResizeTimer);

			nmcResizeTimer = setTimeout(() => {
				const nextMode = getNmcViewportMode();

				if (nextMode !== nmcViewportMode) {
					window.location.reload();
				}
			}, 16);
		},
		{ passive: true },
	);
})();
